"use client"

import { useCallback, useMemo } from "react"
import { useSearchParams, usePathname } from "next/navigation"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/shared/components/ui/tabs"
import { useAuthStore } from "@/features/auth/store"
import {
  usePublicProfile,
  useUserGalleryPosts,
  useUserCharacters,
  useUserLikedPosts,
} from "@/features/profile/hooks/queries/use-public-profile"
import { canViewPrivateCharacters } from "@/features/profile/character-visibility"
import { ProfileHeader } from "./profile-header"
import { CreationsGrid, CharacterGrid, LikesGrid } from "./profile-grid"
import { Sparkles, Loader2 } from "lucide-react"
import { Icon } from "@/shared/components/icons"
import { useLocalizedRouter } from "@/shared/hooks/use-localized-router"
import { useTranslations } from "next-intl"

const VALID_TABS = ["creations", "characters", "likes"] as const
type TabValue = (typeof VALID_TABS)[number]

function isValidTab(value: string | null): value is TabValue {
  return VALID_TABS.includes(value as TabValue)
}

interface ProfilePageContentProps {
  username: string
}

export function ProfilePageContent({ username }: ProfilePageContentProps) {
  const t = useTranslations("profile.page")
  const router = useLocalizedRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const currentProfile = useAuthStore((s) => s.profile)
  const currentUserId = useAuthStore((s) => s.user?.id)

  const { data: profile, isLoading: profileLoading } = usePublicProfile(username)

  const isOwnProfile =
    !!currentProfile?.username &&
    currentProfile.username.toLowerCase() === username.toLowerCase()

  const userId = profile?.id
  const { data: galleryPosts = [], isLoading: postsLoading } = useUserGalleryPosts(userId)
  // Owner check by username AND id before asking for private rows.
  const { data: characters = [], isLoading: charsLoading } = useUserCharacters(userId, {
    includePrivate: canViewPrivateCharacters({
      viewerUsername: currentProfile?.username,
      viewerId: currentUserId,
      profileUsername: username,
      profileId: userId,
    }),
  })
  const { data: likedPosts = [], isLoading: likesLoading } = useUserLikedPosts(userId)

  // Read tab from URL ?tab= param, or auto-select the first tab with content
  const urlTab = searchParams.get("tab")
  const allLoaded = !postsLoading && !charsLoading && !likesLoading

  const autoTab = useMemo((): TabValue => {
    if (!allLoaded) return "creations"
    if (galleryPosts.length > 0) return "creations"
    if (characters.length > 0) return "characters"
    if (likedPosts.length > 0) return "likes"
    return "creations"
  }, [allLoaded, galleryPosts.length, characters.length, likedPosts.length])

  const activeTab: TabValue = isValidTab(urlTab) ? urlTab : autoTab

  // Sync tab changes to URL
  const handleTabChange = useCallback(
    (value: string) => {
      const params = new URLSearchParams(searchParams.toString())
      if (value === "creations") {
        params.delete("tab")
      } else {
        params.set("tab", value)
      }
      const qs = params.toString()
      router.replace(`${pathname}${qs ? `?${qs}` : ""}`, { scroll: false })
    },
    [router, pathname, searchParams]
  )

  if (profileLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
        <div className="mb-3 flex size-16 items-center justify-center rounded-full border border-border bg-muted">
          <Icon name="user" size={28} className="size-7 text-muted-foreground" />
        </div>
        <h2 className="text-lg font-semibold text-foreground">{t("userNotFoundTitle")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("userNotFoundDescription")}
        </p>
      </div>
    )
  }

  const tabPanelClass = "mt-1 -mx-4 px-0 md:-mx-6"

  return (
    <div className="mx-auto w-full max-w-2xl pb-20 lg:max-w-6xl">
      <div className="px-4 md:px-6">
        <ProfileHeader
          profile={profile}
          isOwnProfile={isOwnProfile}
          stats={{
            creations: galleryPosts.length,
            characters: characters.length,
            likes: likedPosts.length,
          }}
        />
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-4">
        <div className="px-4 md:px-6">
          <TabsList variant="line" className="w-full justify-center border-b border-border">
            <TabsTrigger value="creations" className="flex items-center gap-1.5 px-4 py-2.5">
              <Icon name="category" size={16} className="size-4" />
              {t("tabs.creations")}
            </TabsTrigger>
            <TabsTrigger value="characters" className="flex items-center gap-1.5 px-4 py-2.5">
              <Sparkles className="size-4" />
              {t("tabs.characters")}
            </TabsTrigger>
            <TabsTrigger value="likes" className="flex items-center gap-1.5 px-4 py-2.5">
              <Icon name="heart" size={16} className="size-4" />
              {t("tabs.likes")}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="creations" className={tabPanelClass}>
          <CreationsGrid posts={galleryPosts} isLoading={postsLoading} />
        </TabsContent>

        <TabsContent value="characters" className={tabPanelClass}>
          <CharacterGrid characters={characters} isLoading={charsLoading} />
        </TabsContent>

        <TabsContent value="likes" className={tabPanelClass}>
          <LikesGrid posts={likedPosts} isLoading={likesLoading} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
